/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent
} from '@dnd-kit/core';
import {arrayMove, SortableContext} from '@dnd-kit/sortable';
import Button from '@material-ui/core/Button';
import {makeStyles} from '@material-ui/core/styles';
import {SettingsBackupRestoreRounded} from '@material-ui/icons';
import isEmpty from 'lodash.isempty';
import {useCurrentGlobalGroupId} from '@ion/applications/src/hooks/useCurrentGlobalGroupId';
import {errorOptions, successOptions} from '@ion/applications/src/prompts/constants';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  BulkActionEntity,
  BulkActionType,
  CRUDPermissions,
  Device,
  Entity,
  GenericObject
} from '@ion/core/src/types/entities';
import {HierLevel} from '@ion/core/src/types/services';
import {booleanToString} from '@ion/core/src/utils/BooleanUtils';
import {getCurrentDateTime} from '@ion/core/src/utils/DateUtil';
import {
  getDeviceIdentifierFromType,
  getRadioFirmware,
  getScriptFileName,
  getVersionByPlatformId,
  isLMUDevice as isValidLMUDevice,
  isMDTDevice as isValidMDTDevice,
  PLATFORM_IDS
} from '@ion/core/src/utils/DeviceUtils';
import {itemsToCSV} from '@ion/core/src/utils/StringUtil';
import TertiaryButton from '@ion/dls/src/components/buttons/TertiaryButton';
import ErrorSnackbar from '@ion/dls/src/components/snackbars/ErrorSnackbar';
import {useSpaciousTooltipStyles} from '@ion/dls/src/components/tooltip/InfoTooltip';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {ACTIONS} from '@ion/dls/src/editors/features/helpers';
import {useIdentifierResults} from '@ion/dls/src/hooks/useIdentifierResults';
import {InfoIcon} from '@ion/dls/src/icons/InfoIcon';
import UserNotificationDialog from '@ion/dls/src/notifications/UserNotificationDialog';
import Render from '@ion/dls/src/render/Render';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import {translateStatus} from '@ion/dls/src/utils/ComponentUtils';
import {useBulkOperation} from '@ion/query/src/hooks/useBulkOperation';
import {useCreateJob} from '@ion/query/src/hooks/useCreateJob';
import useDeviceManufacturingInfo from '@ion/query/src/hooks/useDeviceManufacturingInfo';
import {useFileHistoryCurrent} from '@ion/query/src/hooks/useFileHistoryCurrent';
import {useDeviceViewErrorMessage} from '../../hooks/useDeviceViewErrorMessage';
import {useDeviceViewRefresh} from '../../hooks/useDeviceViewRefresh';
import useVehicleModuleFeatures from '../../hooks/useVehicleModuleFeatures';
import {toggleUserPrompt} from '../../store/actions/user-prompt';
import {devicesViewSelector} from '../../store/selectors/devices';
import {Vehicle} from '../vehicle/types';
import AssociatedDevicesSection from './AssociatedDevicesSection';
import DeviceDetailsFavoritedItem from './DeviceDetailsFavoritedItem';
import DeviceDetailsGridItem from './DeviceDetailsGridItem';
import DeviceDetailsGridSection from './DeviceDetailsGridSection';
import DeviceDialogGroupDetails from './DeviceDialogGroupDetails';
import {getParentSortableElement} from './helpers';
import {useDeviceVbusStatus} from './useDeviceVbusStatus';
import {useDeviceVehicleProfile} from './useDeviceVehicleProfile';
import useValidateDeviceType from './useValidateDeviceType';
import {useVbusEnabledAccess} from './useVbusEnabledAccess';
import {
  getAVLAddress,
  getDeviceIdentifier,
  getFirstIdReport,
  getMDN,
  getMIN,
  vbusStatusText
} from './ViewDeviceEditorUtils';

export interface DeviceDetailsTabType {
  onSuccess?: () => void;
  tabIndex: Number;
  device: Device;
  deviceDetail: Device;
  isCurrentDeviceMDT: boolean;
  devicesPermissions?: CRUDPermissions;
  Features?: GenericObject;
  scriptDesc?: string;
  isVbuFwEnabled?: boolean;
  deviceNote?: String;
  hasVinReadFeature?: boolean;
  onViewInVehicleManager?: (device: Device) => void;
}

interface StylesProps {
  isVBusOff?: boolean;
}

type GridItems = Map<string, JSX.Element>;

const useStyles = makeStyles(() => ({
  vbusText : {
    ...dlsAlt('typography', 'label2'),
    color : ({
      isVBusOff
    }: StylesProps) => isVBusOff ? dlsColor('caRed') : dlsColor('grey1'),
    fontSize      : '16px',
    letterSpacing : 0,
    padding       : '0 20px'
  },
  vehicleProfileText : {
    ...dlsAlt('typography', 'label2'),
    color         : dlsColor('grey1'),
    fontSize      : '16px',
    letterSpacing : 0,
    padding       : '0 20px'
  },
  buttonContainer : {
    display         : 'flex',
    justifyContent  : 'space-between',
    alignItems      : 'center',
    backgroundColor : dlsColor('white'),
    boxSizing       : 'border-box',
    minHeight       : '56px',
    fontSize        : '16px',
    letterSpacing   : 0,
    padding         : '10px 11px'
  },
  button : {
    color    : dlsColor('aaBlue'),
    fontSize : 12,
    height   : 'auto'
  },
  clearFavoritedItemsButton : {
    margin : '0px 16px 0px auto'
  },
  clearFavoritedItemsSection : {
    display    : 'flex',
    alignItems : 'center',
    gap        : '2px'
  }
}));

const DEVICE_ADDITIONAL_INFO_LOCALE_PATH = 'admin-devices:view.additionalInfo';

const DISABLED_REASON_LOCALE_PATH = 'admin-devices:view.status.disabledReason';

const firstReason = (reasons: [boolean, string][]): string => (
  reasons?.find(([applies]) => applies)?.[1] || ''
);

const reasonTooltip = (reason: string): string => (
  reason ? translate(`${DISABLED_REASON_LOCALE_PATH}.${reason}`) : ''
);

const FAVORITED_ITEMS_PREFERENCE_PATH = 'devices:view.deviceDetailSummary.favoritedItemIds';

/**
 * DeviceDetailsTab component that displays the Device data
 * for the selected Device Details tab.
 */
export const DeviceDetailsTab = ({
  onSuccess,
  devicesPermissions,
  device,
  deviceDetail,
  isCurrentDeviceMDT,
  Features,
  tabIndex = 0,
  scriptDesc,
  isVbuFwEnabled = false,
  hasVinReadFeature = false,
  onViewInVehicleManager
}: DeviceDetailsTabType) => {
  const [isVBusOff, setIsVBusOff] = useState<boolean>(false);
  const classes                   = useStyles({isVBusOff});
  const spaciousTooltipClasses    = useSpaciousTooltipStyles();
  const dispatch                  = useDispatch();
  const accountId                 = useCurrentGlobalGroupId();
  const canHaveVbusAccess         = useVbusEnabledAccess(Features);
  const {hasUpdatePermission}     = devicesPermissions || {};
  const deviceIdentifier          = getDeviceIdentifierFromType(device);
  
  const vehicleFeatures           = useVehicleModuleFeatures(Features as GenericObject);
  const checkProfileFeature       = vehicleFeatures?.find(item => item.name === Vehicle.ProfileManager);
  const hasProfileManagerFeature  = Boolean(checkProfileFeature?.modules?.some((module) => (
    (module?.name === ACTIONS.READ || module?.name === ACTIONS.CREATE) && module?.hasPermission
  )));

  const savedFavoritedItemIds = useMemo(() => UserPreferences.get(FAVORITED_ITEMS_PREFERENCE_PATH), []);

  const [favoritedItemIds, setFavoritedItemIds]     = useState<string[]>(savedFavoritedItemIds ?? []);
  const favoritedItemIdsRef                         = useRef(favoritedItemIds);
  const [activeDragId, setActiveDragId]             = useState<string | null>(null);
  const [draggingItemWidth, setDraggingItemWidth]   = useState<number | null>(null);
  const [showClearFavorites, setShowClearFavorites] = useState(false);

  const {
    assetData,
    result: currentDevice
  } = useSelector(devicesViewSelector);

  const {
    isMDTDevice,
    isLMUDevice
  } = useValidateDeviceType(device) || {};

  const {
    currentFileHistory,
    refresh: onRefreshFileHistoryCurrent,
    isError: isCurrentFileHistoryError
  } = useFileHistoryCurrent(deviceIdentifier);

  const scriptName = getScriptFileName(currentFileHistory);

  const resolvedDeviceId = device?.id || deviceDetail?.id;

  const {
    status    : vbusStatus,
    mode      : vbusMode,
    hasStatus : hasVbusStatus,
    isLoading : isVbusLoading,
    isError   : isVbusError,
    refresh   : refreshVbusStatus
  } = useDeviceVbusStatus({
    deviceId      : resolvedDeviceId,
    initialDevice : device
  });

  const {
    vin        : vinValue,
    isTestVin,
    hasProfile : hasVehicleProfile,
    isLoading  : isVehicleProfileLoading,
    isError    : isVehicleProfileError,
    refresh    : refreshVehicleProfileStatus
  } = useDeviceVehicleProfile({
    deviceId            : resolvedDeviceId,
    initialDevice       : device,
    initialDeviceDetail : deviceDetail
  });

  const loadingText             = translate('common:states.loading');
  const vehicleProfile          = hasVehicleProfile
    ? translate(`admin-devices:grid.vehicleProfile.${isTestVin ? 'test' : 'production'}`)
    : '';
  const isTestVehicleProfile    = Boolean(isTestVin);
  const canSwitchVehicleProfile = Boolean(hasUpdatePermission && hasProfileManagerFeature);
  const vbusStatusDisplay       = isVbusLoading && !hasVbusStatus ? loadingText : vbusStatus;
  const vbusModeDisplay         = isVbusLoading && !vbusMode ? loadingText : vbusMode;
  const vehicleProfileDisplay   = isVehicleProfileLoading && !hasVehicleProfile
    ? loadingText
    : vehicleProfile;

  const {
    manufacturingInfo,
    refresh: onRefreshManufacturingInfo,
    isError: isManufacturingInfoError
  } = useDeviceManufacturingInfo(deviceIdentifier, isCurrentDeviceMDT, !!deviceIdentifier);

  const refreshTab = useCallback(() => {
    onRefreshFileHistoryCurrent();
    onRefreshManufacturingInfo();
  }, []);

  const shouldRenderVinLink = Boolean(
    (hasVinReadFeature || hasProfileManagerFeature) && vinValue && onViewInVehicleManager
  );

  const onVinClick = useCallback(() => {
    onViewInVehicleManager?.(device);
  }, [device, onViewInVehicleManager]);

  useDeviceViewRefresh(refreshTab);

  const {
    doShowErrorMessage,
    errorMessage,
    clearErrorMessage
  } = useDeviceViewErrorMessage([
    [isCurrentFileHistoryError, 'admin-devices:view.deviceDetailSummary.fileHistoryErrorMessage'],
    [isManufacturingInfoError, 'admin-devices:view.deviceDetailSummary.manufacturingInfoErrorMessage'],
    [isVbusError, 'admin-devices:view.deviceDetailSummary.vbusStatusErrorMessage'],
    [isVehicleProfileError, 'admin-devices:view.deviceDetailSummary.vehicleProfileErrorMessage']
  ]);

  const {
    itemToDisplay,
    setShowDialog,
    itemLength,
    showDialog,
    moreItem
  } = useIdentifierResults({
    data           : device?.groups,
    displayedItems : 1
  }) || {};

  const associatedDeviceIdentifier = useMemo(() => {
    let filteredMobileIds          = '';
    const associatedDevices        = device?.extensions?.associatedDevices;
    const associatedDevicesIsArray = Array.isArray(associatedDevices);

    if (associatedDevicesIsArray) {
      const getMobileIdsByType = (isValidFn) => {
        return associatedDevices
          ?.filter(item => isValidFn({category: item?.deviceCategory}))
          ?.map(item => item?.mobileId);
      };

      const validLMUMobileIds = getMobileIdsByType(isValidLMUDevice);
      const validMDTMobileIds = getMobileIdsByType(isValidMDTDevice);
  
      filteredMobileIds = [...validLMUMobileIds, ...validMDTMobileIds].join(',');
    }
    return {
      label : translate('admin-devices:view.generalDeviceInfo.associatedDevices.esn', {returnObjects: true}),
      value : filteredMobileIds
    };
  }, [device, isValidLMUDevice, isValidMDTDevice]);

  const sentinelDevices = useMemo(() => {
    const associatedDevices        = device?.extensions?.associatedDevices;
    const associatedDevicesIsArray = Array.isArray(associatedDevices);
    if (associatedDevicesIsArray) {
      return associatedDevices?.filter(device =>
        device?.tags?.some(tag => tag?.startsWith('ddsar-'))
      );
    }
  }, [associatedDeviceIdentifier]);
  
  const vbusButtonText = useMemo(() => (
    translate(`admin-devices:view.vbus.${vbusStatus === vbusStatusText.off ? 'turnOn' : 'turnOff'}`)
  ), [vbusStatus]);

  const vehicleProfileKey        = isTestVehicleProfile ? 'switchToProduction' : 'switchToTest';
  const vehicleProfileButtonText = useMemo(() => (
    translate(`admin-devices:view.vehicleProfile.${vehicleProfileKey}`)
  ), [vehicleProfileKey]);

  const obdControlName = useMemo(() => (
    translate(`admin-devices:view.vbus.${vbusStatus === vbusStatusText.off ? 'obdControlOn' : 'obdControlOff'}`)
  ), [vbusStatus]);

  const deviceTags = useMemo(() => {
    const assets = assetData?.assets;
    if (assets?.length && isMDTDevice) {
      return assets.map(asset => asset?.title);
    }
    return itemsToCSV(assets, 'title') || [];
  }, [isMDTDevice, assetData]) as string[];

  const {
    save,
    isSuccess,
    isError,
    isLoading
  } = useCreateJob(device?.id);

  const {
    process: processVehicleProfileChange,
    isProcessing: isVehicleProfileProcessing
  } = useBulkOperation();

  const vbusDisabledReason = firstReason([
    [!hasUpdatePermission, 'noUpdatePermission'],
    [isVbusLoading, 'loadingVbusStatus'],
    [isLoading, 'vbusChangeInProgress'],
    [!hasVbusStatus, 'noVbusStatus']
  ]);

  const vehicleProfileDisabledReason = firstReason([
    [!accountId, 'noAccountSelected'],
    [isVehicleProfileLoading, 'loadingVehicleProfile'],
    [isVehicleProfileProcessing, 'vehicleProfileChangeInProgress'],
    [!hasVehicleProfile, 'noVehicleProfile']
  ]);

  useEffect(() => {
    if (isSuccess || isError) {
      dispatch(toggleUserPrompt({
        ...(isSuccess ? successOptions : errorOptions),
        message : translate(`admin-devices:view.vbus.${isSuccess ? 'success' : 'error'}Message`,
          {entity: translate('common:entityLabels.device')})
      }));
      if (isSuccess) {
        refreshVbusStatus();
        onSuccess?.();
      }
    }
  }, [isSuccess, isError, refreshVbusStatus]);

  useEffect(() => {
    setIsVBusOff(vbusStatus === vbusStatusText.off);
  }, [vbusStatus]);

  useEffect(() => {
    favoritedItemIdsRef.current = favoritedItemIds;
  }, [favoritedItemIds]);

  useEffect(() => {
    return () => {
      UserPreferences.set(FAVORITED_ITEMS_PREFERENCE_PATH, favoritedItemIdsRef.current);
    };
  }, []);

  const updateVbusStatus = useCallback(() => {
    const {account} = device;
    const identifier = getDeviceIdentifier(device);
    const accountId = entityId(account);
    const status = vbusStatus === vbusStatusText.off ? vbusStatusText.on : vbusStatusText.off;
    const newJob = {
      groups      : null,
      description : '',
      name        : `${identifier} ${translate('admin-devices:view.vbus.entity')} ${getCurrentDateTime()}`,
      account     : account,
      criteria    : [{
        memberSearch : {
          searchTerms : {
            account : accountId,
            esn     : identifier
          },
          type : Entity.device
        }
      }],
      tasks : [{
        task : {
          description : '',
          name        : `${identifier} / ${obdControlName}`,
          taskType    : translate('admin-devices:view.vbus.taskType'),
          metaData    : {
            commandType : translate('admin-devices:view.vbus.commandType'),
            obdControl  : {
              action : status
            }
          },
          account : account
        }
      }
      ]
    };

    save({job: newJob});
  }, [device, vbusStatus]);

  const switchVehicleProfile = useCallback(() => {
    const canSwitchProfile = Boolean(resolvedDeviceId && accountId);
    const {
      successMessage,
      errorMessage,
      taskName
    } = translate('admin-devices:view.vehicleProfile', {returnObjects: true});
    const taskNickName = `${taskName}: 1 ${translate('common:entityLabels.devices')}; ${getCurrentDateTime()}`;
    const showPrompt = (message: string, options = successOptions) => {
      dispatch(toggleUserPrompt({
        ...options,
        message
      }));
    };
    if (canSwitchProfile) {
      processVehicleProfileChange?.({
        entityIdentifierList : [resolvedDeviceId],
        entityIdentifierType : 'id',
        taskNickName,
        bulkOperation        : {
          actionType : BulkActionType.VEHICLE_PROFILE,
          action     : !isTestVehicleProfile
        },
        entityType : BulkActionEntity.DEVICES,
        hierLevel  : HierLevel.ALL,
        accountId
      }, {
        onSuccess : () => {
          showPrompt(successMessage);
          refreshVehicleProfileStatus();
          onSuccess?.();
        },
        onError : (err) => {
          error('Unable to switch the vehicle profile due to: ', err);
          showPrompt(errorMessage, errorOptions);
        }
      });
    }
  }, [
    accountId,
    resolvedDeviceId,
    dispatch,
    isTestVehicleProfile,
    onSuccess,
    processVehicleProfileChange,
    refreshVehicleProfileStatus
  ]);

  const checkIsItemFavorited = useCallback((id) => favoritedItemIds.includes(id), [favoritedItemIds]);

  const handleFavoriteClicked = useCallback((id) => {
    if (checkIsItemFavorited(id)) {
      setFavoritedItemIds(favoritedItemIds.filter(favoritedItemId => favoritedItemId !== id));
    } else {
      setFavoritedItemIds([...favoritedItemIds, id]);
    }
  }, [favoritedItemIds]);

  /**
   * Prepares a basic grid item component for rendering.
   */
  const augmentField = useCallback(({
    id,
    component,
    isInFavoriteSection = false,
    isDragging = false
  }: {id: string, component: JSX.Element, isInFavoriteSection?: boolean, isDragging?: boolean}): JSX.Element => {
    if (component) {
      const key = isDragging ? null : id;
      const newComponent = React.cloneElement(component, {
        key             : key,
        onFavoriteClick : () => handleFavoriteClicked(id),
        isFavorited     : checkIsItemFavorited(id),
        isBeingDragged  : isDragging
      });
      if (isInFavoriteSection) {
        return <DeviceDetailsFavoritedItem {...newComponent.props}
          key={key}
          id={id}
          widthOverride={isDragging ? draggingItemWidth : undefined} />;
      }
      return newComponent;
    }
    return undefined;
  }, [handleFavoriteClicked, checkIsItemFavorited, draggingItemWidth]);

  const augmentGridItems = useCallback((gridItems?: GridItems) => {
    if (gridItems) {
      return Array.from(gridItems.entries()).map(([id, component]) =>
        augmentField({id, component})
      );
    }
  }, [augmentField]);

  const clearFavoritedItems = useCallback(() => {
    setFavoritedItemIds([]);
    hideClearFavoritesConfirmation();
  }, []);

  const handleClearFavoritedItemsButtonClick = useCallback(() => {
    setShowClearFavorites(true);
  }, []);

  const hideClearFavoritesConfirmation = useCallback(() => {
    setShowClearFavorites(false);
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
    const target      = event.activatorEvent?.target as HTMLElement | null;
    const itemElement = target ? getParentSortableElement(target) : null;
    setDraggingItemWidth(itemElement?.clientWidth ?? null);
  }, []);

  const handleDragEnd = function(event: DragEndEvent) {
    const {active, over} = event;

    if (over && active.id !== over.id) {
      const oldIndex = favoritedItemIds.indexOf(active.id as string);
      const newIndex = favoritedItemIds.indexOf(over.id as string);
      setFavoritedItemIds(arrayMove(favoritedItemIds, oldIndex, newIndex));
    }

    setActiveDragId(null);
    setDraggingItemWidth(null);
  };

  const generalSectionGridItems: GridItems = useMemo(() => new Map([
    ['generalDeviceInfo.deviceName',
      <DeviceDetailsGridItem label={translate('admin-devices:view.generalDeviceInfo.deviceName')}
        value={deviceDetail?.name || device?.name} />],
    ['generalDeviceInfo.deviceId',
      <DeviceDetailsGridItem label={translate('admin-devices:view.generalDeviceInfo.deviceId')}
        value={device?.id} />],
    ['generalDeviceInfo.account',
      <DeviceDetailsGridItem label={translate('admin-devices:view.generalDeviceInfo.account')}
        value={deviceDetail?.account?.title || device?.account?.title} />],
    ['generalDeviceInfo.deviceConfiguration',
      <DeviceDetailsGridItem label={translate('admin-devices:view.generalDeviceInfo.deviceConfiguration')}
        value={deviceDetail?.deviceConfiguration?.title || device?.deviceConfiguration?.title} />],
    ['deviceDetailSummary.assetName',
      <DeviceDetailsGridItem label={translate('admin-devices:view.deviceDetailSummary.assetName')}
        value={device?.asset?.title} />],
    ['deviceDetailSummary.deviceStatus',
      <DeviceDetailsGridItem label={translate('admin-devices:view.deviceDetailSummary.deviceStatus')}
        value={translateStatus(deviceDetail?.status || device?.status)} />],
    ['generalDeviceInfo.firstIDReport',
      <DeviceDetailsGridItem label={translate('admin-devices:view.generalDeviceInfo.firstIDReport')}
        value={getFirstIdReport(device)} />],
    ['generalDeviceInfo.lastReportedAvlAddress',
      <DeviceDetailsGridItem label={translate('admin-devices:view.generalDeviceInfo.lastReportedAvlAddress')}
        value={getAVLAddress(device)} />],
    ['generalDeviceInfo.groups',
      <DeviceDetailsGridItem label={translate('admin-devices:view.generalDeviceInfo.groups')}
        valueToCopyOverride={device?.groups?.[0]?.title ?? null}
        value={<DeviceDialogGroupDetails groupName={device?.groups?.[0]?.title}
          groups={device?.groups}
          deviceName={device?.name}
          itemToDisplay={itemToDisplay}
          setShowDialog={setShowDialog}
          itemLength={itemLength}
          showDialog={showDialog}
          moreItem={moreItem} />} />],
    ['additionalInfo.iccid',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.iccid`)}
        value={device?.iccid} />],
    ['additionalInfo.imsi',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.imsi`)}
        value={device?.imsi} />],
    ['additionalInfo.phone',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.phone`)}
        value={getMDN(device)} />],
    ['additionalInfo.phoneMin',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.phoneMin`)}
        value={getMIN(device)} />],
    ['additionalInfo.partDesc',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.partDesc`)}
        mdSpacing={12}
        value={manufacturingInfo?.Description || manufacturingInfo?.description} />],
    ['generalDeviceInfo.scriptName-sentinel',
      <DeviceDetailsGridItem label={translate('admin-devices:view.generalDeviceInfo.scriptName')}
        mdSpacing={12}
        value={scriptName || ''}
        doRender={(!!associatedDeviceIdentifier || !isEmpty(sentinelDevices)) && (isMDTDevice || isLMUDevice)} />],
    ['generalDeviceInfo.scriptDescription-sentinel',
      <DeviceDetailsGridItem label={translate('admin-devices:view.generalDeviceInfo.scriptDescription')}
        mdSpacing={12}
        value={scriptDesc || ''}
        doRender={(!!associatedDeviceIdentifier || !isEmpty(sentinelDevices)) && (isMDTDevice || isLMUDevice)} />],
    ['associatedDevicesSection',
      <Render.If condition={associatedDeviceIdentifier || !isEmpty(sentinelDevices)}>
        <AssociatedDevicesSection associatedDevices={associatedDeviceIdentifier}
          sentinelDevices={sentinelDevices}
          isLMUDevice={isLMUDevice}
          deviceTags={deviceTags} />
      </Render.If>],
    ['generalDeviceInfo.deviceTag',
      <DeviceDetailsGridItem label={translate('admin-devices:view.generalDeviceInfo.deviceTag')}
        value={deviceTags}
        doRender={!(associatedDeviceIdentifier || !isEmpty(sentinelDevices))} />],
    ['generalDeviceInfo.buildNumber',
      <DeviceDetailsGridItem label={translate('admin-devices:view.generalDeviceInfo.buildNumber')}
        value={device?.idReport?.device?.idReport?.buildNumber || manufacturingInfo?.buildNumber} />],
    ['generalDeviceInfo.scriptName-deviceType',
      <DeviceDetailsGridItem label={translate('admin-devices:view.generalDeviceInfo.scriptName')}
        mdSpacing={12}
        value={scriptName || ''}
        doRender={!((isMDTDevice || isLMUDevice) && associatedDeviceIdentifier)} />],
    ['generalDeviceInfo.scriptDescription-deviceType',
      <DeviceDetailsGridItem label={translate('admin-devices:view.generalDeviceInfo.scriptDescription')}
        mdSpacing={12}
        value={scriptDesc || ''}
        doRender={!((isMDTDevice || isLMUDevice) && associatedDeviceIdentifier)} />]
  ]), [
    associatedDeviceIdentifier,
    device,
    deviceDetail,
    deviceTags,
    isLMUDevice,
    isMDTDevice,
    itemLength,
    itemToDisplay,
    manufacturingInfo,
    moreItem,
    scriptDesc,
    scriptName,
    sentinelDevices,
    showDialog
  ]);

  const statusSectionGridItems: GridItems = useMemo(() => new Map([
    ['status.vbusEnableStatus',
      <DeviceDetailsGridItem label={translate('admin-devices:view.status.vbusEnableStatus')}
        bodyOverride={
          <div className={classes.buttonContainer}>
            <div className={classes.vbusText}>
              {vbusStatusDisplay}
            </div>
            <Render.If condition={canHaveVbusAccess}>
              <Tooltip arrow
                externalClasses={spaciousTooltipClasses}
                title={reasonTooltip(vbusDisabledReason)}>
                <span>
                  <Button className={classes.button}
                    variant='outlined'
                    disabled={Boolean(vbusDisabledReason)}
                    onClick={updateVbusStatus}>
                    {vbusButtonText}
                  </Button>
                </span>
              </Tooltip>
            </Render.If>
          </div>
        } />],
    ['status.vehicleProfile',
      <DeviceDetailsGridItem label={translate('admin-devices:view.status.vehicleProfile')}
        bodyOverride={
          <div className={classes.buttonContainer}>
            <div className={classes.vehicleProfileText}>
              {vehicleProfileDisplay}
            </div>
            <Render.If condition={canSwitchVehicleProfile}>
              <Tooltip arrow
                externalClasses={spaciousTooltipClasses}
                title={reasonTooltip(vehicleProfileDisabledReason)}>
                <span>
                  <Button className={classes.button}
                    variant='outlined'
                    disabled={Boolean(vehicleProfileDisabledReason)}
                    onClick={switchVehicleProfile}>
                    {vehicleProfileButtonText}
                  </Button>
                </span>
              </Tooltip>
            </Render.If>
          </div>
        }/>],
    ['status.vbusMode',
      <DeviceDetailsGridItem label={translate('admin-devices:view.status.vbusMode')}
        value={vbusModeDisplay} />],
    ['status.configParamReadError',
      <DeviceDetailsGridItem label={translate('admin-devices:view.status.configParamReadError')}
        value={booleanToString(device?.idReport?.isUnitStatus1)} />],
    ['status.isGPSAntennaStatusOK',
      <DeviceDetailsGridItem label={translate('admin-devices:view.status.isGPSAntennaStatusOK')}
        value={booleanToString(device?.idReport?.isGPSAntennaStatusOK)} />],
    ['status.isGPSTrackingOK',
      <DeviceDetailsGridItem label={translate('admin-devices:view.status.isGPSTrackingOK')}
        value={booleanToString(device?.idReport?.isGPSTrackingOK)} />],
    ['status.isGPSReceiverTestOK',
      <DeviceDetailsGridItem label={translate('admin-devices:view.status.isGPSReceiverTestOK')}
        value={booleanToString(device?.idReport?.isGPSReceiverTestOK)} />]
  ]), [
    canHaveVbusAccess,
    device,
    vbusDisabledReason,
    vehicleProfileDisabledReason,
    vehicleProfileButtonText,
    canSwitchVehicleProfile,
    switchVehicleProfile,
    vehicleProfileDisplay,
    deviceDetail,
    updateVbusStatus,
    vbusButtonText,
    vbusModeDisplay,
    vbusStatusDisplay
  ]);

  const additionalInfoSectionGridItems: GridItems = useMemo(() => new Map([
    ['additionalInfo.esn',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.esn`)}
        value={deviceDetail?.esn || device?.esn} />],
    ['additionalInfo.serialNumber',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.serialNumber`)}
        value={deviceDetail?.serialNumber || device?.serialNumber} />],
    ['additionalInfo.airId',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.airId`)}
        value={deviceDetail?.airId || device?.airId} />],
    ['additionalInfo.imei',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.imei`)}
        value={device?.imei} />],
    ['additionalInfo.imei2',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.imei2`)}
        value={device?.imei2} />],
    ['additionalInfo.isdn',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.isdn`)}
        value={device?.isdn} />],
    ['additionalInfo.meid',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.meid`)}
        value={device?.meid} />],
    ['additionalInfo.partInfo',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.partInfo`)}
        value={manufacturingInfo?.UNITPARTNUMBER || manufacturingInfo?.unitpartNumber ||
            manufacturingInfo?.partNumber} />],
    ['additionalInfo.carrierStatus',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.carrierStatus`)}
        value={device?.carrierStatus} />],
    ['additionalInfo.vehicleClass',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.vehicleClass`)}
        value={device?.idReport?.device?.idReport?.vehicleClass} />],
    ['additionalInfo.vin',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.vin`)}
        value={vinValue}
        clickTooltip={shouldRenderVinLink ? translate('admin-devices:view.additionalInfo.vinTooltip') : ''}
        onClick={shouldRenderVinLink ? onVinClick : undefined} />],
    ['additionalInfo.ble3Firmware',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.ble3Firmware`)}
        value={getVersionByPlatformId(device, PLATFORM_IDS.ble3Firmware)} />],
    ['additionalInfo.vbu3Firmware',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.vbu3Firmware`)}
        value={getVersionByPlatformId(device, PLATFORM_IDS.vbu3Firmware)}
        doRender={!isVbuFwEnabled}/>],
    ['additionalInfo.radioFirmware',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.radioFirmware`)}
        value={getRadioFirmware(device)} />
    ],
    ['additionalInfo.note',
      <DeviceDetailsGridItem label={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.note`)}
        mdSpacing={12}
        value={deviceDetail?.deviceNote || (currentDevice as Device)?.deviceNote} />]
  ]), [
    currentDevice,
    device,
    deviceDetail,
    isVbuFwEnabled,
    manufacturingInfo,
    onVinClick,
    shouldRenderVinLink,
    vinValue
  ]);

  const allSectionsGridItems = [
    generalSectionGridItems,
    statusSectionGridItems,
    additionalInfoSectionGridItems
  ];

  const getGridItemFromId = (id: string) => {
    if (id) {
      for (const gridItems of allSectionsGridItems) {
        if (gridItems.has(id)) {
          return gridItems.get(id);
        }
      }
      error(`Tried to get a nonexistent grid item with ID '${id}' in DeviceDetailsTab.`);
    }
  };

  const favoritesSectionInfoTooltip = (
    <Tooltip arrow
      title={<>
        <p>{translate('admin-devices:view.deviceDetailSummary.favoritesSectionInfoTooltip.summary')}</p>
        <p>{translate('admin-devices:view.deviceDetailSummary.favoritesSectionInfoTooltip.description')}</p>
      </>}>
      <span>
        <InfoIcon size={IconSize.smallMedium} />
      </span>
    </Tooltip>
  );

  const clearFavoritedItemsButton = useMemo(() => (
    <TertiaryButton onClick={handleClearFavoritedItemsButtonClick}
      fontSize='14px'
      className={classes.clearFavoritedItemsButton}>
      <div className={classes.clearFavoritedItemsSection}>
        <SettingsBackupRestoreRounded />
        {translate('admin-devices:view.deviceDetailSummary.clearFavoritedItemsButtonText')}
      </div>
    </TertiaryButton>
  ), [handleClearFavoritedItemsButtonClick]);

  const favoritesSectionHeaderAttachments = (
    <>
      {favoritesSectionInfoTooltip}
      {clearFavoritedItemsButton}
    </>
  );

  return (
    <div id={`detailsTab_${tabIndex}`}>
      <Render.If condition={favoritedItemIds.length > 0}>
        <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <SortableContext items={favoritedItemIds}>
            <DeviceDetailsGridSection sectionTitle={translate('admin-devices:view.deviceDetailSummary.favoritesSectionTitle')}
              headerAttachments={favoritesSectionHeaderAttachments}>
              {favoritedItemIds.map(id => (
                augmentField({id, component: getGridItemFromId(id), isInFavoriteSection: true})
              ))}
            </DeviceDetailsGridSection>
          </SortableContext>
          <DragOverlay>
            <Render.If condition={activeDragId !== null}>
              {augmentField({
                id                  : activeDragId,
                component           : getGridItemFromId(activeDragId),
                isInFavoriteSection : true,
                isDragging          : true
              })}
            </Render.If>
          </DragOverlay>
        </DndContext>
        <UserNotificationDialog show={showClearFavorites}
          capitalize
          title={translate('admin-devices:view.deviceDetailSummary.clearFavoritedItemsConfirmationTitle')}
          message={translate('admin-devices:view.deviceDetailSummary.clearFavoritedItemsConfirmationMessage')}
          actionLabel={translate('common:editors.shared.yesContinue')}
          dismissLabel={translate('common:editors.shared.neverMind')}
          onAction={clearFavoritedItems}
          onDismiss={hideClearFavoritesConfirmation} />
      </Render.If>

      <DeviceDetailsGridSection sectionTitle={translate('common:form.asset.general')}>
        {augmentGridItems(generalSectionGridItems)}
      </DeviceDetailsGridSection>

      <DeviceDetailsGridSection sectionTitle={translate('admin-devices:view.status.statusTitle')}>
        {augmentGridItems(statusSectionGridItems)}
      </DeviceDetailsGridSection>

      <DeviceDetailsGridSection sectionTitle={translate(`${DEVICE_ADDITIONAL_INFO_LOCALE_PATH}.title`)}>
        {augmentGridItems(additionalInfoSectionGridItems)}
      </DeviceDetailsGridSection>

      <ErrorSnackbar show={doShowErrorMessage}
        message={errorMessage}
        onDismiss={clearErrorMessage} />
    </div>
  );
};

export default DeviceDetailsTab;
